import type { NotebookSummary as ChatNotebookSummary } from "./notebookSnapshot"
import { formatDate } from "./reportClient"
import type { NotebookSummary, Schedule } from "./reportTypes"

export interface NotebookRow {
  id: string
  title: string
  created_at: string
  created_by: string | null
  kind: "chat" | "generated" | "series"
  seriesStorage?: "chat" | "generated"
  revision?: number
  schedule?: Schedule
  editions?: NotebookSummary[]
  template?: NotebookSummary
  scheduleKnown?: boolean
  scheduleLoading?: boolean
  schedulingAvailable?: boolean
  historyPartial?: boolean
}

export interface GenerativeSchedule extends Schedule {
  template_notebook_id: string
  edition_ids: string[]
}

export interface NotebookCatalog {
  notebooks: NotebookSummary[]
  schedules?: Schedule[]
  generative_schedules?: GenerativeSchedule[]
  scheduling_available?: boolean
}

/** Match only catalog lineage and notebooks visible in the current workspace. */
export function buildChatNotebookRows(
  chats: ChatNotebookSummary[],
  catalog: NotebookCatalog,
  currentUserEmail: string | null = null,
  scheduleLoading = false,
): NotebookRow[] {
  const byId = new Map(chats.map((chat) => [chat.id, chat]))
  const used = new Set<string>()
  const series: NotebookRow[] = []
  for (const schedule of catalog.generative_schedules ?? []) {
    const template = byId.get(schedule.template_notebook_id)
    const editions = schedule.edition_ids
      .map((id) => byId.get(id))
      .filter((item): item is ChatNotebookSummary => item !== undefined)
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    if (!template && editions.length === 0) {
      continue
    }
    if (template) {
      used.add(template.id)
    }
    editions.forEach((edition) => used.add(edition.id))
    series.push({
      id: `chat-schedule:${schedule.id}`,
      title: template?.title ?? schedule.name,
      created_at: editions[0]?.created_at ?? template?.created_at ?? "",
      created_by: template?.created_by ?? editions[0]?.created_by ?? null,
      kind: "series",
      seriesStorage: "chat",
      schedule,
      editions,
      template,
      scheduleKnown: true,
      schedulingAvailable: catalog.scheduling_available,
      historyPartial: editions.length !== schedule.edition_ids.length,
    })
  }
  return [
    ...chats
      .filter((chat) => !used.has(chat.id))
      .map(
        (chat): NotebookRow => ({
          ...chat,
          kind: "chat",
          scheduleLoading,
          // The lineage catalog is owner-scoped; it cannot classify other users' shared notebooks.
          scheduleKnown:
            catalog.generative_schedules !== undefined &&
            Boolean(currentUserEmail) &&
            chat.created_by?.toLowerCase() === currentUserEmail?.toLowerCase(),
        }),
      ),
    ...series,
  ]
}

/** Groups only persisted schedule identities; matching names never establish membership. */
export function buildNotebookSeries(
  catalog: NotebookCatalog,
  creator: string | null,
): NotebookRow[] {
  const schedules = catalog.schedules ?? []
  const ids = new Set(schedules.map((schedule) => schedule.id))
  const templates = new Set(
    schedules.map((schedule) => schedule.template_notebook_id),
  )
  const generated: NotebookRow[] = catalog.notebooks
    .filter(
      (notebook) =>
        !templates.has(notebook.id) && !ids.has(notebook.schedule_id ?? ""),
    )
    .map((notebook) => ({
      ...notebook,
      created_by: creator,
      kind: "generated",
      scheduleKnown: catalog.schedules !== undefined,
    }))
  return [
    ...generated,
    ...schedules.map((schedule): NotebookRow => {
      const editions = catalog.notebooks
        .filter((notebook) => notebook.schedule_id === schedule.id)
        .sort(
          (a, b) =>
            Date.parse(b.period?.end ?? b.created_at) -
            Date.parse(a.period?.end ?? a.created_at),
        )
      const template = catalog.notebooks.find(
        (notebook) => notebook.id === schedule.template_notebook_id,
      )
      const latestSaved = editions.reduce<string | undefined>(
        (latest, edition) =>
          latest === undefined ||
          Date.parse(edition.created_at) > Date.parse(latest)
            ? edition.created_at
            : latest,
        undefined,
      )
      return {
        id: `schedule:${schedule.id}`,
        title: template?.title ?? schedule.name,
        created_at:
          latestSaved ??
          template?.created_at ??
          schedule.created_at ??
          schedule.updated_at ??
          "",
        created_by: creator,
        kind: "series",
        schedule,
        editions,
        template,
        scheduleKnown: true,
        schedulingAvailable: catalog.scheduling_available,
      }
    }),
  ]
}

export function notebookScheduleLabel(row: NotebookRow): string {
  if (!row.schedule) {
    return row.scheduleKnown
      ? "One-off"
      : row.scheduleLoading
        ? "Checking schedule…"
        : "Schedule unavailable"
  }
  const schedule = row.schedule
  if (
    schedule.execution_mode === "manual" ||
    schedule.status === "manual_only"
  ) {
    return "Manual recipe"
  }
  const cadence =
    { daily: "Daily", weekly: "Weekly", monthly: "Monthly" }[
      schedule.cadence
    ] ?? schedule.cadence
  if (!schedule.enabled) {
    return `${cadence} · Paused`
  }
  if (row.schedulingAvailable === false) {
    return `${cadence} · Unavailable`
  }
  return cadence
}

/** Reporting periods end at the following midnight; show the last included calendar day. */
export function notebookPeriodLabel(period: {
  start: string
  end: string
}): string {
  const start = period.start.slice(0, 10)
  const end = new Date(`${period.end.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(end.getTime())) {
    return `${formatDate(start)} – ${formatDate(period.end)}`
  }
  end.setUTCDate(end.getUTCDate() - 1)
  const lastDay = end.toISOString().slice(0, 10)
  return start === lastDay
    ? formatDate(start)
    : `${formatDate(start)} – ${formatDate(lastDay)}`
}
